import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Grid, Card, Message, Divider, List, Icon } from 'semantic-ui-react';
import '../Playbook.css';

class SalesActivityExplained extends Component {
    render() {
        return (
            <Container fluid className="playbook-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Sales Activity
                </h1>
                <div className="ui divider"></div>

                <Grid stackable>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Segment className="playbook-content">
                                <Header as="h2" className="section-header">Introduction</Header>
                                
                                <p>
                                    By now you have probably built out a message via the Multiply application, built a customer 
                                    pitch deck, and are at various stages of training sales team members to carry the message 
                                    effectively over the phone, in a casual setting, in a board room, or over a video conference. 
                                    Now this is where we start talking about how we as sales leaders manage our teams to 
                                    <strong> QUALITY of activity</strong> and not just <strong>QUANTITY of activity</strong>.
                                </p>

                                <Message info>
                                    <Message.Header>The Management Shift</Message.Header>
                                    <p>
                                        Traditional sales management focuses on counting activities. Effective sales management 
                                        focuses on the quality and outcomes of those activities. This shift changes everything.
                                    </p>
                                </Message>

                                <Divider />

                                <Header as="h2" className="section-header">CRM Tools and Activity Tracking</Header>
                                
                                <p>
                                    We are often asked what flavor of CRM tool we prefer or recommend. Right now we don't have 
                                    a preference. We don't have any partnerships, so we recommend that companies do their due 
                                    diligence and pick the one they see their team most likely using. They all have their 
                                    strengths and weaknesses. Pick one that best fits your team and ensures that you are 
                                    requiring information that is meaningful and not just information for the sake of collecting information.
                                </p>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>
                                            <Icon name="database" />
                                            CRM Tool Fundamentals
                                        </Card.Header>
                                        <Card.Description>
                                            <p>
                                                A CRM tool is only as good as the information it contains and will only be culturally 
                                                adopted if the information is meaningful to the sales team and sales leadership.
                                            </p>
                                            
                                            <p>The intent of CRM tools is to manage sales teams. These tools are very good at collecting 
                                            sales activity and calculating metrics. Among other things, at its core, a CRM system will tell you:</p>
                                            
                                            <List bulleted>
                                                <List.Item><strong>Who your sales team is talking to</strong> (contacts)</List.Item>
                                                <List.Item><strong>How many leads</strong> your team is following up on</List.Item>
                                                <List.Item><strong>How many opportunities</strong> your team is managing and where they are in the funnel</List.Item>
                                                <List.Item><strong>How large your pipeline is</strong> and where that is in conjunction with goals</List.Item>
                                            </List>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={8}>
                                            <Card fluid color="red" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>What CRM Tools Assume</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>CRM tools make assumptions as it relates to sales management. Those assumptions are that the sales team knows:</p>
                                                        <List bulleted>
                                                            <List.Item><strong>What to say</strong> - The right message for each situation</List.Item>
                                                            <List.Item><strong>When to say it</strong> - Proper timing and context</List.Item>
                                                            <List.Item><strong>How to say it</strong> - Effective delivery and presentation</List.Item>
                                                        </List>
                                                        <p><strong>These are the assumptions we at Multiply fill in.</strong></p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={8}>
                                            <Card fluid color="green" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>The Multiply Difference</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>While CRM tools track the "what" and "how many," Multiply addresses the "how" and "what to say":</p>
                                                        <List bulleted>
                                                            <List.Item><strong>Message Framework</strong> - Consistent, proven messaging</List.Item>
                                                            <List.Item><strong>Delivery Training</strong> - How to present effectively</List.Item>
                                                            <List.Item><strong>Meeting Control</strong> - Managing prospect interactions</List.Item>
                                                            <List.Item><strong>Quality Assessment</strong> - Evaluating encounter effectiveness</List.Item>
                                                        </List>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">The Problem with Activity-Only Management</Header>
                                
                                <p>
                                    Now, a common misconception around sales management is that QUANTITY of activity creates sales. 
                                    That certainly can be true. It is fantastic to have a calendar full of really good sales encounters. 
                                    But we all know that not all encounters are created equal. Some are "good" meetings while some are 
                                    (and we don't mean this harshly) "not good" meetings.
                                </p>

                                <Message warning>
                                    <Message.Header>The Activity Trap</Message.Header>
                                    <p>
                                        The problem with slanting too heavily to activity (leads, pipeline size, meetings, contacts, 
                                        emails sent, calls made, etc.) is that a salesperson can do TONS of the "right things" 
                                        and produce really bad results. When someone is doing all the right things and not producing, 
                                        it can be difficult to manage that person successfully.
                                    </p>
                                </Message>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>What We Know About Quality Sales Activity</Card.Header>
                                        <Card.Description>
                                            <p>If a salesperson knows what to say, when to say it, and how to say it, then their activity will pick up as will the QUALITY of their encounters.</p>
                                            
                                            <p>When we create sales people that are comfortable controlling meetings and are comfortable handling objections, then they will:</p>
                                            
                                            <Grid stackable>
                                                <Grid.Row>
                                                    <Grid.Column width={8}>
                                                        <List bulleted>
                                                            <List.Item><strong>Create more encounters</strong> - Confidence drives activity</List.Item>
                                                            <List.Item><strong>Get better encounters</strong> - Quality preparation improves outcomes</List.Item>
                                                            <List.Item><strong>Don't fear rejection</strong> - Preparation reduces anxiety</List.Item>
                                                            <List.Item><strong>Don't fear embarrassment</strong> - Training builds confidence</List.Item>
                                                        </List>
                                                    </Grid.Column>
                                                    <Grid.Column width={8}>
                                                        <List bulleted>
                                                            <List.Item><strong>Don't fear making mistakes</strong> - Framework provides security</List.Item>
                                                            <List.Item><strong>Don't fear getting "stuck"</strong> - Training covers objections</List.Item>
                                                            <List.Item><strong>Control meeting flow</strong> - Structure creates comfort</List.Item>
                                                            <List.Item><strong>Ask for next steps</strong> - Clear process drives results</List.Item>
                                                        </List>
                                                    </Grid.Column>
                                                </Grid.Row>
                                            </Grid>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">The "Hire and Hope Strategy"</Header>

                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={8}>
                                            <Card fluid color="red" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="warning sign" />
                                                        The Ineffective Approach
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>If your approach is to:</p>
                                                        <List ordered>
                                                            <List.Item>Stand up a CRM tool</List.Item>
                                                            <List.Item>Hire the "best" salespeople</List.Item>
                                                            <List.Item>Measure activity</List.Item>
                                                            <List.Item>Calculate results</List.Item>
                                                        </List>
                                                        
                                                        <p><strong>Then your approach is what we call the "Hire and Hope Strategy".</strong></p>
                                                        
                                                        <p>It can work but will generate VERY inconsistent results across a large sales team. 
                                                        This is why we have the 80/20 rule of sales (80% of sales comes from 20% of the team).</p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={8}>
                                            <Card fluid color="green" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="checkmark" />
                                                        The Systematic Approach
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>A better approach combines:</p>
                                                        <List bulleted>
                                                            <List.Item><strong>CRM Tools</strong> - For tracking and metrics</List.Item>
                                                            <List.Item><strong>Message Framework</strong> - Consistent communication</List.Item>
                                                            <List.Item><strong>Training Program</strong> - Skill development</List.Item>
                                                            <List.Item><strong>Quality Assessment</strong> - Meaningful measurement</List.Item>
                                                            <List.Item><strong>Ongoing Coaching</strong> - Continuous improvement</List.Item>
                                                        </List>
                                                        
                                                        <Message compact>
                                                            <p>This creates predictable, scalable results across the entire team.</p>
                                                        </Message>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">Multiply Analogy: Driving with Confidence</Header>
                                
                                <Message>
                                    <Message.Header>Sales Performance Comparison</Message.Header>
                                    <Grid stackable>
                                        <Grid.Row>
                                            <Grid.Column width={8}>
                                                <strong>The Unprepared Salesperson:</strong>
                                                <p>
                                                    The unprepared and overly cautious salesperson is the person driving 45 MPH in the center 
                                                    lane of a major highway at rush hour. The salesperson has a death grip on the steering 
                                                    wheel and is nervous—and then they make a mistake. That mistake causes cars to bounce 
                                                    off each other and traffic to back up for miles. It's a mess.
                                                </p>
                                            </Grid.Column>
                                            
                                            <Grid.Column width={8}>
                                                <strong>The Prepared Salesperson:</strong>
                                                <p>
                                                    The prepared salesperson is in the 1500 HP race car driving up Pikes Peak. They are 
                                                    expertly taking on the hairpin curves, throwing gravel over the cliffs. They are in 
                                                    total control. While no drive is perfect, this driver is good enough to overcome any 
                                                    mistakes or obstacles and make it to the top.
                                                </p>
                                            </Grid.Column>
                                        </Grid.Row>
                                    </Grid>
                                </Message>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>Our Management Philosophy</Card.Header>
                                        <Card.Description>
                                            <p>
                                                When we hire a salesperson, we have to assume they are unprepared, nervous, and mistake-prone. 
                                                Our job is to build them to a state of comfort with prospects such that they act with speed and control.
                                            </p>
                                            
                                            <p>
                                                We can do that. And when we do, we are able to have meaningful and honest conversations about 
                                                the encounters instead of just focusing on the quantity of encounters.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">Quality AND Quantity</Header>
                                
                                <p>
                                    So far, we have talked about the benefits of CRM tools and their ability to count and track 
                                    activity and metrics, and we introduced the concept of managing to quality meetings rather 
                                    than only quantity. Let's dig a little deeper and define what we mean by quality.
                                </p>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Defining Quality Meetings</Card.Header>
                                        <Card.Description>
                                            <p>
                                                When we say quality, we are talking about a meeting where we can define what we are asking for 
                                                and know how to execute the conversation to get something from the prospect. It is NOT always 
                                                about selling something.
                                            </p>
                                            
                                            <p>Obviously there are different levels of "quality." The highest level involves an encounter 
                                            with a prospect who fits inside of our desired prospect profiles and is a potential buyer. 
                                            But since that isn't always the case, there are other forms of "quality" such as:</p>
                                            
                                            <List bulleted>
                                                <List.Item><strong>Meetings with Centers of Influence (COI)</strong> - Building referral networks</List.Item>
                                                <List.Item><strong>Meetings with prospects outside of our desired profiles</strong> with the intent of personal development</List.Item>
                                                <List.Item><strong>Meetings with prospects that may not be potential buyers</strong> but with which you are testing a message to determine product market fit for a new offering</List.Item>
                                            </List>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message success>
                                    <Message.Header>Key Principle</Message.Header>
                                    <p>
                                        Whether you are a salesperson or a manager over a sales team, the important things are to 
                                        know and to be honest about the type of encounter and to be intentional about the preparation.
                                    </p>
                                </Message>

                                <Header as="h3" className="subsection-header">Quality Assessment Questions</Header>
                                
                                <p>Here are the questions that sales leaders should ask the sales team and that salespeople should ask themselves when determining the quality of the meeting:</p>
                                
                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Primary Question</Card.Header>
                                        <Card.Description>
                                            <p><strong>Are we meeting with a potential buyer?</strong></p>
                                            <p>The answer to this question is easily verified by determining if the prospect fits into one of the prospect profiles documented in the Multiply Application.</p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>If the Answer is No</Card.Header>
                                        <Card.Description>
                                            <p><strong>What are we trying to get out of the meeting?</strong></p>
                                            <p>The answer to this question is:</p>
                                            <p><strong>Personal development</strong>, meaning we don't waste time. If we have a meeting and it is not with a potential buyer, then we can classify it as personal development rather than business development. This gives the sales team an opportunity to practice some portion of the story that they are not comfortable with. This should be guided by the "grading" and feedback within the Multiply Application.</p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">When Meeting with a Center of Influence (COI)</Header>
                                
                                <p>
                                    If you are meeting with a COI, then the preparation looks much the same as when you are meeting 
                                    with a potential buyer. The meeting still could involve personal development, but we are also 
                                    looking to get something out of it.
                                </p>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>COI Value Creation</Card.Header>
                                        <Card.Description>
                                            <p>
                                                Based on us designating the meeting to be with a COI, we know the prospect is outside of 
                                                the company's primary prospect profiles. However, it is different than a meeting with a 
                                                non-buyer that we treat as personal development. A COI can create value through introductions 
                                                or partnerships. Therefore, we anticipate our asks appropriately.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message warning>
                                    <Message.Header>The COI Ask</Message.Header>
                                    <p>These asks often are not offering-specific. In general, the ask for a COI is:</p>
                                    <p><strong>"Are there people in your network that may be good prospects for us to speak with?"</strong></p>
                                    <p>
                                        Now this is a pretty heavy ask. Think about it: You are asking someone who doesn't know you 
                                        to make introductions for you or to provide you with names. This is why you have to be VERY 
                                        prepared for COI meetings.
                                    </p>
                                </Message>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>Earning COI Trust</Card.Header>
                                        <Card.Description>
                                            <p>
                                                Very often, you have to first earn the trust of a COI. This is where it is important to 
                                                offer up references and reinforce the consistency and preparation described and implemented 
                                                throughout the Multiply framework. We can't emphasize that enough. We approach the market consistently.
                                            </p>
                                            <p>
                                                So you can often earn an introduction from a COI by telling the COI that you would execute 
                                                meetings with that introduction in the same way you have with the COI. If you have done a 
                                                good job with the meeting, this can often instill enough confidence with the COI to earn you introductions.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">When Meeting With a Buyer</Header>
                                
                                <p>
                                    Now back to the original question: Are we meeting with a potential buyer? If the answer is yes, 
                                    then we have to prepare very intentionally. We have to make sure that the sales team member knows:
                                </p>

                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <Card fluid>
                                                <Card.Content>
                                                    <Card.Header>Essential Preparation Elements</Card.Header>
                                                    <Card.Description>
                                                        <List bulleted>
                                                            <List.Item>
                                                                <strong>The overall messaging.</strong> We need to make sure that the sales team member is 
                                                                anticipating the type of encounter and the method in which he/she is going to deliver 
                                                                the message. However, we must be ready for changes in plans. If technology fails, you 
                                                                need a means to present which switches the style from presentation mode to more conversational. 
                                                                If the prospect invites you to sit around a round table vs. presenting in the board room, 
                                                                you need to be prepared for that.
                                                            </List.Item>
                                                            <List.Item>
                                                                <strong>The prospect profile and the pivot points to utilize based on the characteristics of the prospect.</strong> 
                                                                Remember, we don't change the company story based on the type of prospect. Our company doesn't change. 
                                                                However, there are characteristics about the prospects in various profiles that we cater to.
                                                            </List.Item>
                                                            <List.Item>
                                                                <strong>The asks.</strong> We will most likely know which offerings the prospect is most 
                                                                likely to be interested in based on the profile of the prospect. If the sales team member 
                                                                is selling for a company with a wide array of offerings, the asks will likely be more 
                                                                targeted based on the anticipated offering interest rather than focused on the more general asks.
                                                            </List.Item>
                                                        </List>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Message info>
                                    <Message.Header>Meeting Control Philosophy</Message.Header>
                                    <p>
                                        When we enter a meeting with a prospect, we may lose control of the setting but we must be 
                                        able to still control the meeting and deliver. Therefore, we anticipate the situation and 
                                        prepare for alternatives.
                                    </p>
                                </Message>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Verification and Preparation</Card.Header>
                                        <Card.Description>
                                            <p>
                                                There are certainly times where we can make the correct assumption that an experienced 
                                                salesperson knows the message. However, as a sales leader, you have to be comfortable 
                                                verifying that this is true if you are not 100% certain the salesperson knows the messaging.
                                            </p>
                                            <p>
                                                The asks vary from encounter to encounter. These are things that you should discuss before 
                                                every encounter. Sometimes you are asking a more overarching ask that leads to a conversation 
                                                that allows the salesperson to get to the offering. Other times, you anticipate asks based 
                                                on the prospect profile.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">Multiply Tip: Targeting Your Offerings</Header>
                                
                                <Message>
                                    <Message.Header>Understanding Our Three Offerings</Message.Header>
                                    <p>At present, we have three offerings:</p>
                                    <List bulleted>
                                        <List.Item><strong>SaaS product</strong></List.Item>
                                        <List.Item><strong>Modular approach</strong></List.Item>
                                        <List.Item><strong>Framework implementation</strong></List.Item>
                                    </List>
                                </Message>

                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={8}>
                                            <Card fluid>
                                                <Card.Content>
                                                    <Card.Header>Strategic Questioning</Card.Header>
                                                    <Card.Description>
                                                        <p>We can know how to craft our asks based on who we are meeting with. If we are meeting with an individual company, you need to ask yourself these things:</p>
                                                        <p><strong>Do we have a documented sales message, as well as the ability to maintain it and verify the team knows how to convey it?</strong></p>
                                                        <p>That answer is generally no, which leads us to the conversation about the details of each offering to determine which one fits best.</p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={8}>
                                            <Card fluid>
                                                <Card.Content>
                                                    <Card.Header>Profile-Based Targeting</Card.Header>
                                                    <Card.Description>
                                                        <p>We also know that certain prospect profiles are more susceptible to offers than others:</p>
                                                        <List bulleted>
                                                            <List.Item><strong>Non-funded startups:</strong> Not going to buy the full framework implementation. They can't afford it and generally don't need it. They need the messaging module and MAYBE the onboarding module.</List.Item>
                                                            <List.Item><strong>Enterprise companies:</strong> Much more likely to buy the full framework implementation because they know they have the problem but don't have the manpower to resolve it—and they have the money to pay to fix the problem.</List.Item>
                                                        </List>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>Sales Leadership Conversations</Card.Header>
                                        <Card.Description>
                                            <p>
                                                These are the types of conversations you as the sales leader will have with your sales team. 
                                                You can then measure the effectiveness of the conversation based on how the prospect reacts 
                                                to the intentionally planned asks and to the salesperson's ability to ask the qualifying 
                                                questions and get to the proposal phase.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">Internal Preparation – Pre and Post Meeting</Header>
                                
                                <p>
                                    When running an internal sales meeting, we have all sorts of information to track as it relates 
                                    to pipeline metrics. Those are great. We like those. But what we want is actionable information 
                                    that results from very intentional questions.
                                </p>

                                <Header as="h3" className="subsection-header">Pre-Meeting Questions</Header>
                                
                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Essential Pre-Meeting Focus Areas</Card.Header>
                                        <Card.Description>
                                            <p>We encourage the sales leaders and sales team members to focus on the following questions when looking at future meetings:</p>
                                            <List ordered>
                                                <List.Item><strong>Who are we meeting with?</strong></List.Item>
                                                <List.Item><strong>What are we saying?</strong></List.Item>
                                                <List.Item><strong>What are we asking for?</strong></List.Item>
                                                <List.Item><strong>What is our desired next step?</strong></List.Item>
                                            </List>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message success>
                                    <Message.Header>Meeting Success Definition</Message.Header>
                                    <p>
                                        We don't have "good" or "bad" meetings based on good conversations or a non-buyer. We have 
                                        good meetings because we have a plan and we execute it well. Being very intentional about 
                                        these questions makes following up on the "success" or "failure" of the meeting very simple.
                                    </p>
                                </Message>

                                <Header as="h3" className="subsection-header">Post-Meeting Evaluation</Header>
                                
                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Post-Meeting Assessment Questions</Card.Header>
                                        <Card.Description>
                                            <p>The salesperson or leadership must ask themselves:</p>
                                            <List bulleted>
                                                <List.Item><strong>Did we say the things we intended to say?</strong></List.Item>
                                                <List.Item><strong>Did we get a chance to ask as intended?</strong></List.Item>
                                                <List.Item><strong>Did we get to a desired next step?</strong></List.Item>
                                                <List.Item><strong>Did we identify the problem that we solve?</strong></List.Item>
                                                <List.Item><strong>Are we able to submit a proposal?</strong></List.Item>
                                            </List>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>Next Step Control</Card.Header>
                                        <Card.Description>
                                            <p>
                                                We need to approach every encounter with a plan that includes what we want to happen next. 
                                                Ultimately, we want to get to a point where we have "permission" to write a proposal. 
                                                (See our "Proposal" chapter for more information on this topic.)
                                            </p>
                                            <p>
                                                The point is, we have meetings, and the goal is to be able to get to a point where we 
                                                describe how we are going to work with the prospect. We obviously don't get to this step 
                                                in every meeting. So we have to understand the prospect's next steps or anticipate the 
                                                next meeting request with new stakeholders.
                                            </p>
                                            <p>
                                                There really is an infinite set of scenarios. However, we have to be able to definitively 
                                                say, "This is what is happening next." And we have to have some part of the control of 
                                                that next step. If we don't, then we get into the infinitely long sales cycle that never ends.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Header as="h3" className="subsection-header">Opportunity Management Questions</Header>
                                
                                <Message info>
                                    <Message.Header>Managing Sales Opportunities</Message.Header>
                                    <p>
                                        Assuming we know what our teams are supposed to say—because we have defined and trained 
                                        to it—and also assuming we are being very intentional about preparation and getting to 
                                        next steps, we should be asking ourselves these questions when managing an opportunity:
                                    </p>
                                </Message>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Three Critical Questions</Card.Header>
                                        <Card.Description>
                                            <List ordered>
                                                <List.Item><strong>Have we told them the story?</strong></List.Item>
                                                <List.Item><strong>Have we identified the problem that we solve?</strong></List.Item>
                                                <List.Item><strong>Have we issued a proposal?</strong></List.Item>
                                            </List>
                                            <p>
                                                If our teams can't answer affirmatively to these steps, then there is more sales work 
                                                to do, more people to tell the story to, and more next steps to set. Or it's time to 
                                                disqualify the opportunity and move on.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Divider />

                                <Header as="h2" className="section-header">Time Management for Salespeople</Header>
                                
                                <p>
                                    Companies with mature sales teams can have various roles. However, MANY companies of all stages 
                                    from concept to enterprise have a salesperson (or persons) who is responsible for the complete 
                                    sales cycle, from creating the lead to closing. That can range from a founder to a true salesperson.
                                </p>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>Revenue Responsibility Model</Card.Header>
                                        <Card.Description>
                                            <p>
                                                In this model, the salesperson is responsible for revenue, which means managing existing 
                                                accounts and creating new ones.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Header as="h3" className="subsection-header">The Three Time Buckets</Header>
                                
                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <Message warning>
                                                <Message.Header>Time Allocation Framework</Message.Header>
                                                <p>In this model, there are three (3) "buckets" where sales team members (or those responsible for revenue—the founders) spend their time:</p>
                                            </Message>
                                        </Grid.Column>
                                    </Grid.Row>
                                    
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column width={4}>
                                            <Card fluid color="red" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>1. The Busy Bucket</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>Administrative tasks, internal meetings, paperwork</p>
                                                        <p><strong>Goal: Minimize this bucket</strong></p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={6}>
                                            <Card fluid color="blue" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>2. The Prospecting Bucket</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>Finding new leads, cold outreach, networking</p>
                                                        <p><strong>Goal: Consistent dedication</strong></p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={6}>
                                            <Card fluid color="green" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>3. The Protecting and Growing Current Accounts Bucket</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p>Account management, upselling, relationship maintenance</p>
                                                        <p><strong>Goal: Scheduled attention</strong></p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>Time Management Strategy</Card.Header>
                                        <Card.Description>
                                            <p>
                                                We will talk more about numbers 2 and 3 in the "Prospecting" section of this module, 
                                                but we all have to know and understand where time is allocated. Everyone deals with 
                                                the "Busy Bucket", but it needs to be minimized.
                                            </p>
                                            <p>
                                                Sales managers and sales team members need to create time in their calendars to dedicate 
                                                to prospecting AND current accounts. Individuals will gravitate to one or the other based 
                                                on their individual comfort level, and therefore both activities require scheduling dedication.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message>
                                    <Message.Header>Final Note</Message.Header>
                                    <p>
                                        This section isn't an advertisement for Multiply—it is meant to help set up the rest of this 
                                        document and the thought process that you as a sales leader have when managing your team.
                                    </p>
                                </Message>

                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Container>
        );
    }
}

export default observer(SalesActivityExplained);